import { useCallback, useMemo } from 'react';
import { KnownSettingsNodeDtoType } from '@groundfloor-platform/mgmt-service-g-sdk';
import { useQueryListProjects } from '../../../../core/queries/projects.queries';
import { useQueryGetHub, useMutationSetAccessedViaProject } from '../../../lockers/hubs.queries';
import { EntityForm, NodeReference } from '../form.types';
import { SettingsActionDispatcher } from '../../reducer/settings-tree.reducer';
import { RelationshipType } from '../../reducer/settings-tree-reducer.types';
import { RelationshipForm } from './form';
import { RelationshipFormOutput } from './form-output';
import { RelationshipFormSchema } from './form-schema';
import { relationshipFormUISchema } from './ui-schema';

export function useCreateRelationshipForm(
  hubEntityId: string | undefined,
  parentProjectId: string | undefined,
  parentNodeId: string | undefined,
  formData: RelationshipForm | undefined,
  relationshipType: RelationshipType = RelationshipType.Unknown
): EntityForm | null {
  const hubQuery = useQueryGetHub(hubEntityId);
  const allProjects = useQueryListProjects();
  const setAccessedViaMutation = useMutationSetAccessedViaProject();

  const hubName = hubQuery.data?.name ?? '';

  const defaultValues = useMemo(
    () => ({ hubName, projectId: parentProjectId } as RelationshipForm),
    [hubName, parentProjectId]
  );

  const initialFormOutput = useMemo(() => new RelationshipFormOutput({}), []);

  const currentFormOutput = useMemo(
    () => new RelationshipFormOutput({ ...initialFormOutput, ...formData }),
    [formData, initialFormOutput]
  );

  const projectOptions = useMemo(() => allProjects.data ?? [], [allProjects.data]);

  const formSchema = useMemo(
    () => RelationshipFormSchema.FromFormData(hubName, projectOptions, relationshipType),
    [hubName, projectOptions, relationshipType]
  );

  const onSubmit = useCallback(async () => {
    if (!hubEntityId || !currentFormOutput.projectId) return;
    await setAccessedViaMutation.mutateAsync({ hubID: hubEntityId, projectID: currentFormOutput.projectId });
    return { entityId: hubEntityId, parentNodeId };
  }, [hubEntityId, currentFormOutput.projectId, parentNodeId, setAccessedViaMutation]);

  const onSubmissionAction = useCallback((dispatch: SettingsActionDispatcher, result: NodeReference | void) => {
    if (!result?.parentNodeId) return;
    const formNodeId = `${result.parentNodeId}-${KnownSettingsNodeDtoType.ProjectLinkFolder}`;
    // Defer until after SUBMIT_FORM_SUCCESS so it removes the placeholder first,
    // then immediately clear the form state so no snackbar is shown.
    setTimeout(() => {
      dispatch({ type: 'ACKNOWLEDGE_FORM_SUCCESS', payload: { nodeId: formNodeId } });
    }, 0);
  }, []);

  if (!formSchema) {
    return null;
  }

  return {
    isLoading: hubQuery.isLoading || allProjects.isLoading,
    title: 'New Relationship',
    defaultValues,
    initialFormOutput,
    formSchema,
    uiSchema: relationshipFormUISchema,
    getOutput(formData) {
      return new RelationshipFormOutput(formData as RelationshipForm);
    },
    onSubmit,
    onSubmissionAction,
    isLocked: false,
    lockedMessage: null
  };
}
